import { Outlet, useLocation } from "react-router-dom";
import { styled } from "styled-components";
import { ConfirmationModal } from "../../core/components/confirmationModal/ConfirmationModal";
import { EventIndicator } from "../../core/components/eventIndicator/EventIndicator";
import { getAuthReady, getLoggingOut } from "../../core/store/appSlice";
import { getChainId, getCurrentPracticeId } from "../../core/store/chainSlice";
import { useMainStore } from "../../core/store/mainStore";
import { IPAD_MINI_BREAKPOINT_NUMBER } from "../../core/styles/breakpoints";
import { useStyles } from "../../core/styles/theme";
import useWindowDimensions from "../../core/utils/useWindowDimensions";
import { Footer } from "./components/Footer";
import { HEADER_HEIGHT } from "./components/Header";
import { HeaderProvider } from "./context/HeaderContext";
import { LoadingScreen } from "./LoadingScreen";
import { useHandleAuthState } from "./utils/useHandleAuthState";
import { useHandleGetChainConfig } from "./utils/useHandleGetChainConfig";
import { useHandleParameterIds } from "./utils/useHandleParameterIds";
import { useStaffFillParentMessages } from "./utils/webAppSession";

export const MainLayout = () => {
  const loggingOut = useMainStore(getLoggingOut);
  const authReady = useMainStore(getAuthReady);
  useHandleAuthState();
  useHandleParameterIds();
  useHandleGetChainConfig();
  useStaffFillParentMessages();
  const width = useWindowDimensions().width;
  const isDesktop = width >= IPAD_MINI_BREAKPOINT_NUMBER;
  const isAppointmentFlow = useLocation().pathname.includes("appointment");
  const showFooter = !isAppointmentFlow || !isDesktop;
  const theme = useStyles();
  const practiceId = useMainStore(getCurrentPracticeId);
  const chainId = useMainStore(getChainId);

  if (!authReady) {
    return <LoadingScreen />;
  }

  if ((!isAppointmentFlow && !practiceId) || !chainId) {
    return <LoadingScreen />;
  }

  return (
    <HeaderProvider>
      <EventIndicator />
      <ConfirmationModal />
      <ContentContainer $showFooter={showFooter}>
        <OutletContainer $showFooter={showFooter}>
          {loggingOut ? <LoadingScreen /> : <Outlet />}
        </OutletContainer>
        {showFooter && (
          <FooterContainer>
            <Footer textColor={theme.colors.dark} />
          </FooterContainer>
        )}
      </ContentContainer>
    </HeaderProvider>
  );
};

const ContentContainer = styled.div<{ $showFooter: boolean }>`
  flex-grow: 1;
  width: 100%;
  max-height: calc(100vh - ${HEADER_HEIGHT}px);
  max-height: calc(100dvh - ${HEADER_HEIGHT}px);
  margin-top: ${HEADER_HEIGHT}px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.background};
`;

const OutletContainer = styled.div<{ $showFooter: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: auto;
`;

const FooterContainer = styled.div`
  width: 100%;
  position: absolute;
  bottom: 0;
`;
